import React, { useState } from 'react';
import { CalendarCheck, DollarSign, ClipboardList, Bell, BookOpen, X, CheckCheck, CheckCircle, Clock, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/common/StatCard';
import { formatDate, formatCurrency, generateId } from '../../utils/helpers';

// Subject color palette
const SUBJECT_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', icon: 'text-blue-500' },
  { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', dot: 'bg-green-500', icon: 'text-green-500' },
  { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500', icon: 'text-purple-500' },
  { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', icon: 'text-orange-500' },
  { bg: 'bg-pink-50', border: 'border-pink-200', badge: 'bg-pink-100 text-pink-700', dot: 'bg-pink-500', icon: 'text-pink-500' },
  { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500', icon: 'text-teal-500' },
];

function HomeworkCard({ hw, subject, colorIdx, student, parent, hwCompletions, setHwCompletions }) {
  const today = new Date().toISOString().split('T')[0];
  const isOverdue = hw.dueDate < today;
  const isDueToday = hw.dueDate === today;
  const color = SUBJECT_COLORS[colorIdx % SUBJECT_COLORS.length];

  // All daily confirmations for this homework by this student
  const completions = hwCompletions.filter(c => c.homeworkId === hw.id && c.studentId === student?.id);
  const todayConfirmation = completions.find(c => c.date === today);
  const [expanded, setExpanded] = useState(false);

  const handleConfirm = () => {
    const entry = {
      id: generateId('HWC'),
      homeworkId: hw.id,
      studentId: student?.id,
      parentId: parent?.id,
      parentName: parent?.name,
      date: today,
      confirmedAt: new Date().toISOString(),
      note: '',
    };
    setHwCompletions(prev => [...prev, entry]);
  };

  const handleUndo = () => {
    setHwCompletions(prev => prev.filter(c => !(c.homeworkId === hw.id && c.studentId === student?.id && c.date === today)));
  };

  return (
    <div className={`rounded-xl border ${color.border} ${color.bg} overflow-hidden`}>
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        <div className={`w-1 self-stretch rounded-full ${color.dot} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold truncate">{hw.title}</h4>
              <p className="mt-0.5 line-clamp-2">{hw.description}</p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className={`font-medium px-2 py-0.5 rounded-full ${
                isOverdue ? 'bg-red-100 text-red-600' :
                isDueToday ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100'
              }`}>
                {isDueToday ? 'Due Today' : isOverdue ? 'Overdue' : `Due ${formatDate(hw.dueDate)}`}
              </span>
              {completions.length > 0 && (
                <button onClick={() => setExpanded(e => !e)} className="text-indigo-600 flex items-center gap-0.5">
                  {completions.length} day{completions.length > 1 ? 's' : ''} confirmed {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 gap-2">
            <span className="">Assigned: {formatDate(hw.assignedDate)}</span>
            {todayConfirmation ? (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-green-700 bg-green-100 px-2 py-1 rounded-lg font-medium">
                  <CheckCircle size={13} /> Confirmed today {new Date(todayConfirmation.confirmedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <button onClick={handleUndo} className="hover:text-red-400 underline">Undo</button>
              </div>
            ) : (
              <button onClick={handleConfirm}
                className="flex items-center gap-1.5 font-semibold bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                <CheckCircle size={13} /> Mark Done Today
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Completion history */}
      {expanded && completions.length > 0 && (
        <div className="px-4 pb-3 pt-0 border-t border-opacity-50" style={{ borderColor: 'inherit' }}>
          <p className="font-medium mb-2 mt-2">Completion History</p>
          <div className="space-y-1">
            {completions.sort((a, b) => b.date.localeCompare(a.date)).map(c => (
              <div key={c.id} className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-green-700">
                  <CheckCircle size={11} /> {formatDate(c.date)}
                </span>
                <span className="text-gray-400">
                  {new Date(c.confirmedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} by {c.parentName || 'Parent'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ParentDashboard() {
  const { students, parents, classes, attendance, feePayments, feeStructures, exams, homework,
    subjects, announcements, notifications, setNotifications, hwCompletions, setHwCompletions } = useApp();
  const { currentUser } = useAuth();

  const parent = parents.find(p => p.id === currentUser?.profileId);
  const myChildren = students.filter(s => parent?.studentIds?.includes(s.id));

  const [selectedChildId, setSelectedChildId] = useState(myChildren[0]?.id || null);
  const child = myChildren.find(c => c.id === selectedChildId) || myChildren[0];
  const childClass = classes.find(c => c.id === child?.classId);
  const childAttendance = attendance.filter(a => a.entityType === 'student' && a.entityId === child?.id);
  const present = childAttendance.filter(a => a.status === 'present').length;
  const attPct = childAttendance.length > 0 ? Math.round(present / childAttendance.length * 100) : 0;
  const myFeeStructure = feeStructures.find(f => f.classId === child?.classId);
  const myPaid = feePayments.filter(p => p.studentId === child?.id).reduce((s, p) => s + p.amount, 0);
  const totalDue = myFeeStructure ? myFeeStructure.totalAmount + (child?.previousYearBalance || 0) : 0;
  const feePending = myFeeStructure ? Math.max(0, totalDue - myPaid) : 0;
  const upcomingExams = exams.filter(e => e.status === 'scheduled' || e.status === 'ongoing').slice(0, 3);
  const myAnnouncements = announcements.filter(a => a.isActive && (a.targetRoles.includes('parent') || a.targetRoles.includes('all')));

  // Active homework for child's class grouped by subject
  const today = new Date().toISOString().split('T')[0];
  const classHomework = homework.filter(h => h.classId === child?.classId && h.status === 'active');

  // Group by subject
  const bySubject = classHomework.reduce((acc, hw) => {
    const subId = hw.subjectId;
    if (!acc[subId]) acc[subId] = [];
    acc[subId].push(hw);
    return acc;
  }, {});
  const subjectIds = Object.keys(bySubject);

  // Today's pending confirmations count
  const todayPending = classHomework.filter(hw => {
    return !hwCompletions.some(c => c.homeworkId === hw.id && c.studentId === child?.id && c.date === today);
  }).length;

  // Unread homework notifications for selected child's class
  const childClassIds = myChildren.map(c => c.classId);
  const hwAlerts = (notifications || []).filter(n =>
    n.type === 'homework' && n.targetRoles.includes('parent') && n.classId === child?.classId
  );
  const unreadAlerts = hwAlerts.filter(n => !n.readBy.includes(currentUser?.id));

  const markRead = (notifId) => {
    setNotifications(prev => prev.map(n =>
      n.id === notifId ? { ...n, readBy: [...n.readBy, currentUser?.id] } : n
    ));
  };
  const markAllRead = () => {
    setNotifications(prev => prev.map(n =>
      (n.type === 'homework' && childClassIds.includes(n.classId) && !n.readBy.includes(currentUser?.id))
        ? { ...n, readBy: [...n.readBy, currentUser?.id] } : n
    ));
  };
  const dismiss = (notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Parent Dashboard</h1>
          {child && <p className="text-gray-500 text-sm mt-0.5">Monitoring: {child.firstName} {child.lastName} | {childClass?.name} - Sec {child.sectionId}</p>}
        </div>
        {myChildren.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {myChildren.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedChildId(c.id)}
                className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors border ${
                  c.id === child?.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white border-gray-300 hover:bg-gray-50'
                }`}
              >
                {c.firstName} {c.lastName}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Child Profile */}
      {child && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Child Profile</h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xl font-bold">{child.firstName?.[0]}{child.lastName?.[0]}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
              {[['Name', child.firstName + ' ' + child.lastName], ['Class', (childClass?.name || '') + '-' + child.sectionId], ['Roll No', child.rollNumber], ['Admission No', child.admissionNumber]].map(([k, v]) => (
                <div key={k}><p className="">{k}</p><p className="text-sm font-semibold">{v}</p></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* New Homework Alerts Banner */}
      {unreadAlerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center">
                <Bell size={16} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900 text-sm">{unreadAlerts.length} New Homework Alert{unreadAlerts.length > 1 ? 's' : ''}</h3>
                <p className="text-amber-600">New homework has been assigned to your child</p>
              </div>
            </div>
            <button onClick={markAllRead} className="flex items-center gap-1 text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg font-medium">
              <CheckCheck size={13} /> Mark all read
            </button>
          </div>
          <div className="space-y-2">
            {unreadAlerts.map(alert => (
              <div key={alert.id} className="flex items-start justify-between bg-white border border-amber-100 rounded-lg p-3 gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen size={15} className="text-orange-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{alert.title}</p>
                    <p className="mt-0.5">{alert.body}</p>
                    <p className="mt-0.5">{new Date(alert.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => markRead(alert.id)} className="text-indigo-600 hover:bg-indigo-50 px-2 py-1 rounded font-medium">Read</button>
                  <button onClick={() => dismiss(alert.id)} className="p-1 hover:bg-red-50 rounded hover:text-red-400"><X size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Attendance" value={attPct + '%'} subtext={present + '/' + childAttendance.length + ' days'} colorClass={attPct >= 75 ? 'bg-green-500' : 'bg-red-500'} />
        <StatCard icon={DollarSign} label="Fee Pending" value={feePending > 0 ? formatCurrency(feePending) : 'Cleared'} subtext="Academic year" colorClass={feePending > 0 ? 'bg-red-500' : 'bg-green-500'} />
        <StatCard icon={ClipboardList} label="Homework" value={classHomework.length} subtext={`${todayPending} pending today`} colorClass="bg-orange-500" />
        <StatCard icon={Bell} label="Announcements" value={myAnnouncements.length} subtext="For parents" colorClass="bg-purple-500" />
      </div>

      {/* Subject-wise Homework with Daily Confirmation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <BookOpen size={18} className="text-indigo-500" />
            Subject-wise Homework
            {classHomework.length > 0 && (
              <span className="bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full">{classHomework.length}</span>
            )}
          </h3>
          {todayPending > 0 && (
            <span className="flex items-center gap-1 font-medium text-amber-700 bg-amber-100 px-3 py-1.5 rounded-lg">
              <AlertCircle size={13} /> {todayPending} pending today
            </span>
          )}
          {todayPending === 0 && classHomework.length > 0 && (
            <span className="flex items-center gap-1 font-medium text-green-700 bg-green-100 px-3 py-1.5 rounded-lg">
              <CheckCheck size={13} /> All confirmed for today
            </span>
          )}
        </div>

        {subjectIds.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">No active homework assigned</p>
        ) : (
          <div className="space-y-5">
            {subjectIds.map((subId, idx) => {
              const sub = subjects.find(s => s.id === subId);
              const subHws = bySubject[subId];
              const color = SUBJECT_COLORS[idx % SUBJECT_COLORS.length];
              const confirmedToday = subHws.filter(hw =>
                hwCompletions.some(c => c.homeworkId === hw.id && c.studentId === child?.id && c.date === today)
              ).length;

              return (
                <div key={subId}>
                  {/* Subject Header */}
                  <div className={`flex items-center justify-between px-3 py-1 rounded-lg mb-2 ${color.bg} border ${color.border}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${color.dot}`} />
                      <span className="text-sm font-semibold">{sub?.name || 'Unknown Subject'}</span>
                      <span className={`px-2 py-0.5 rounded-full font-medium ${color.badge}`}>{subHws.length} task{subHws.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{confirmedToday}/{subHws.length} done today</span>
                    </div>
                  </div>

                  {/* Homework cards for this subject */}
                  <div className="space-y-2 pl-2">
                    {subHws.map(hw => (
                      <HomeworkCard
                        key={hw.id}
                        hw={hw}
                        subject={sub}
                        colorIdx={idx}
                        student={child}
                        parent={parent}
                        hwCompletions={hwCompletions}
                        setHwCompletions={setHwCompletions}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fee Status */}
      {myFeeStructure && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Fee Status</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center"><p className="">Total</p><p className="font-bold">{formatCurrency(totalDue)}</p></div>
            <div className="bg-green-50 rounded-lg p-3 text-center"><p className="text-green-600">Paid</p><p className="font-bold text-green-700">{formatCurrency(myPaid)}</p></div>
            <div className={(feePending > 0 ? 'bg-red-50' : 'bg-green-50') + ' rounded-lg p-3 text-center'}><p className={'' + (feePending > 0 ? 'text-red-600' : 'text-green-600')}>Pending</p><p className={'font-bold ' + (feePending > 0 ? 'text-red-700' : 'text-green-700')}>{feePending > 0 ? formatCurrency(feePending) : 'Cleared'}</p></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Upcoming Exams</h3>
          {upcomingExams.length > 0 ? (
            <div className="space-y-2">
              {upcomingExams.map(exam => (
                <div key={exam.id} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                  <div><p className="text-sm font-medium">{exam.name}</p><p className="">{formatDate(exam.startDate)}</p></div>
                  <span className={'px-2 py-0.5 rounded-full font-medium capitalize ' + (exam.status === 'ongoing' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100')}>{exam.status}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm">No upcoming exams</p>}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Announcements</h3>
          <div className="space-y-2">
            {myAnnouncements.slice(0, 4).map(ann => (
              <div key={ann.id} className="py-2 border-b border-gray-50 last:border-0">
                <p className="font-medium text-sm">{ann.title}</p>
                <p className="mt-0.5">{ann.content.slice(0, 80)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
export default ParentDashboard;
